import { useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend,
  Cell,
} from 'recharts';
import { format } from 'date-fns';
import { useAgentStore } from '../stores/agentStore';
import { useExclusionsStore } from '../stores/exclusionsStore';
import AgentFilterBar from '../components/agents/AgentFilterBar';
import { surfaceColor, surfaceLabel } from '../utils/agentSurfaces';

function StatCard({ label, value, tooltip }: { label: string; value: string; tooltip: string }) {
  return (
    <MuiTooltip title={tooltip} arrow placement="top">
      <Card
        sx={{
          flex: '1 1 0',
          minWidth: 124,
          height: '100%',
          cursor: 'default',
          border: '1px solid rgba(148, 163, 184, 0.12)',
          borderRadius: 2,
          transition: 'transform 0.15s, border-color 0.15s',
          '&:hover': {
            borderColor: 'rgba(139, 92, 246, 0.45)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.2 }}>
            {value}
          </Typography>
        </CardContent>
      </Card>
    </MuiTooltip>
  );
}

export default function AgentsOverview() {
  const { summary, isSummaryLoading, error, dateRange, surfaces, orgIds, userIds, setExclusions, loadSummary } =
    useAgentStore();
  const {
    excludedOrgIds,
    effectiveExcludedUserIds,
    isInitialized: exclusionsInitialized,
  } = useExclusionsStore();

  useEffect(() => {
    // Fail closed: don't fetch until the shared internal/test-org exclusions are
    // resolved, so internal activity is never shown unfiltered. setExclusions
    // writes the store synchronously, so the following loadSummary already sees
    // them (a single fetch, no ordering race).
    if (!exclusionsInitialized) return;
    setExclusions(excludedOrgIds, effectiveExcludedUserIds);
    loadSummary();
  }, [
    dateRange,
    surfaces,
    orgIds,
    userIds,
    excludedOrgIds,
    effectiveExcludedUserIds,
    exclusionsInitialized,
    setExclusions,
    loadSummary,
  ]);

  // Pivot trend rows ({bucket, surface, count}) into stacked-area rows keyed by surface.
  const { trendData, trendSurfaces } = useMemo(() => {
    if (!summary) return { trendData: [], trendSurfaces: [] as string[] };
    const byBucket = new Map<string, Record<string, number | string>>();
    const surfaceSet = new Set<string>();
    for (const point of summary.trend) {
      surfaceSet.add(point.surface);
      const key = point.bucket;
      if (!byBucket.has(key)) byBucket.set(key, { bucket: key });
      byBucket.get(key)![point.surface] = point.count;
    }
    const rows = Array.from(byBucket.values()).sort((a, b) =>
      String(a.bucket).localeCompare(String(b.bucket))
    );
    return { trendData: rows, trendSurfaces: Array.from(surfaceSet) };
  }, [summary]);

  const fmtPct = (v: number | null) => (v == null ? '—' : `${Math.round(v * 100)}%`);
  const fmtNum = (v: number) => v.toLocaleString();
  const fmtLatency = (v: number | null) => (v == null ? '—' : `${v.toFixed(1)}s`);
  const avgTurns =
    summary && summary.totalConversations > 0
      ? (summary.totalInteractions / summary.totalConversations).toFixed(1)
      : '—';

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Agents
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Agent interactions across the app, Excel, Teams, AppFab, email, SMS, WhatsApp, and scheduled runs.
      </Typography>

      <AgentFilterBar />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stat cards — single row on desktop, wraps on small screens */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {[
          {
            label: 'Interactions',
            value: summary ? fmtNum(summary.totalInteractions) : '—',
            tooltip: 'Total agent turns — every message/turn counts once.',
          },
          {
            label: 'Conversations',
            value: summary ? fmtNum(summary.totalConversations) : '—',
            tooltip: 'Distinct agent conversations (threads). Scheduled/one-shot runs count as one.',
          },
          {
            label: 'Active agents',
            value: summary ? fmtNum(summary.uniqueAgents) : '—',
            tooltip: 'Distinct agents used in this range (default Pelles Agent + custom agents).',
          },
          {
            label: 'Active users',
            value: summary ? fmtNum(summary.uniqueUsers) : '—',
            tooltip: 'Distinct users who interacted with an agent.',
          },
          {
            label: 'Success rate',
            value: summary ? fmtPct(summary.successRate) : '—',
            tooltip: 'Share of interactions that completed without error.',
          },
          {
            label: 'Avg latency',
            value: summary ? fmtLatency(summary.avgLatency) : '—',
            tooltip: 'Average end-to-end response time per interaction.',
          },
          {
            label: 'Avg turns / conv',
            value: summary ? avgTurns : '—',
            tooltip: 'Average number of turns per conversation (interactions ÷ conversations).',
          },
        ].map((s) =>
          isSummaryLoading ? (
            <Skeleton
              key={s.label}
              variant="rounded"
              height={76}
              sx={{ flex: '1 1 0', minWidth: 124 }}
            />
          ) : (
            <StatCard key={s.label} {...s} />
          )
        )}
      </Box>

      <Grid container spacing={2}>
        {/* Surface breakdown */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: 360 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Conversations by surface
            </Typography>
            {isSummaryLoading ? (
              <Skeleton variant="rounded" height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={290}>
                <BarChart data={summary?.bySurface ?? []} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="surface"
                    tickFormatter={surfaceLabel}
                    tick={{ fontSize: 12 }}
                    width={70}
                  />
                  <Tooltip
                    formatter={(v) => [Number(v).toLocaleString(), 'Conversations']}
                    labelFormatter={(label) => surfaceLabel(String(label))}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {(summary?.bySurface ?? []).map((s) => (
                      <Cell key={s.surface} fill={surfaceColor(s.surface)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Trend over time */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: 360 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Conversations over time
            </Typography>
            {isSummaryLoading ? (
              <Skeleton variant="rounded" height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={290}>
                <AreaChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                  <XAxis
                    dataKey="bucket"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => {
                      try {
                        return format(new Date(v), 'MMM d');
                      } catch {
                        return v;
                      }
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    labelFormatter={(v) => {
                      try {
                        return format(new Date(v as string | number), 'PP');
                      } catch {
                        return v;
                      }
                    }}
                  />
                  <Legend formatter={(v) => surfaceLabel(v as string)} />
                  {trendSurfaces.map((s) => (
                    <Area
                      key={s}
                      type="monotone"
                      dataKey={s}
                      stackId="1"
                      stroke={surfaceColor(s)}
                      fill={surfaceColor(s)}
                      fillOpacity={0.5}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Agent leaderboard */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Agents
        </Typography>
        {isSummaryLoading ? (
          <Skeleton variant="rounded" height={200} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell align="right">Conversations</TableCell>
                <TableCell align="right">Interactions</TableCell>
                <TableCell align="right">Users</TableCell>
                <TableCell align="right">Success</TableCell>
                <TableCell>Surfaces</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(summary?.byAgent ?? []).map((a) => (
                <TableRow key={a.agentName ?? a.agentId ?? 'unknown'} hover>
                  <TableCell>{a.agentName ?? 'Unknown agent'}</TableCell>
                  <TableCell align="right">{a.conversations.toLocaleString()}</TableCell>
                  <TableCell align="right">{a.interactions.toLocaleString()}</TableCell>
                  <TableCell align="right">{a.uniqueUsers.toLocaleString()}</TableCell>
                  <TableCell align="right">{fmtPct(a.successRate)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {a.surfaces.map((s) => (
                        <Chip
                          key={s}
                          label={surfaceLabel(s)}
                          size="small"
                          sx={{
                            backgroundColor: `${surfaceColor(s)}22`,
                            color: surfaceColor(s),
                            height: 20,
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {(summary?.byAgent ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                    No agent interactions in this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Top organizations */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Top organizations
        </Typography>
        {isSummaryLoading ? (
          <Skeleton variant="rounded" height={200} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Organization</TableCell>
                <TableCell align="right">Interactions</TableCell>
                <TableCell align="right">Conversations</TableCell>
                <TableCell align="right">Users</TableCell>
                <TableCell align="right">Success</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(summary?.byOrg ?? []).map((o) => (
                <TableRow key={o.orgId ?? 'unknown'} hover>
                  <TableCell>{o.orgName ?? o.orgId ?? 'Unknown org'}</TableCell>
                  <TableCell align="right">{o.interactions.toLocaleString()}</TableCell>
                  <TableCell align="right">{o.conversations.toLocaleString()}</TableCell>
                  <TableCell align="right">{o.uniqueUsers.toLocaleString()}</TableCell>
                  <TableCell align="right">{fmtPct(o.successRate)}</TableCell>
                </TableRow>
              ))}
              {(summary?.byOrg ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                    No agent interactions in this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
